import { Card, DescriptionList, Heading, KeyValueRow, Price, Text } from "@/components/ui"
import { useAnimatedCurrency } from "@/hooks/use-animated-currency"
import { DURATION, TRANSITION } from "@/lib/motion"
import { AnimatePresence, motion, useReducedMotion } from "motion/react"
import { useId } from "react"

const MotionKeyValueRow = motion.create(KeyValueRow)

/** Props of `BagSummary`; `subtotal` and `payable` are in reais, `discountCents` is the evaluated discount in centavos. */
export type BagSummaryProps = {
  subtotal: number
  discountCents: number
  payable: number
}

/** Value breakdown at the end of the bag: subtotal, the evaluated discount when there is one, and the total to pay. */
export function BagSummary({ subtotal, discountCents, payable }: BagSummaryProps) {
  const titleId = useId()
  const reduceMotion = useReducedMotion()
  const subtotalLabel = useAnimatedCurrency(subtotal)
  const totalLabel = useAnimatedCurrency(payable)
  const rowTransition = reduceMotion ? { duration: DURATION.none } : TRANSITION.size

  return (
    <Card as="section" padding="md" aria-labelledby={titleId}>
      <Heading level={2} size="card" id={titleId} className="mb-2">
        Resumo de valores
      </Heading>
      <DescriptionList className="gap-0">
        <KeyValueRow size="sm" className="mb-1.5" label="Subtotal" value={<motion.span className="tabular-nums">{subtotalLabel}</motion.span>} />
        <AnimatePresence initial={false}>
          {discountCents > 0 ? (
            <MotionKeyValueRow
              key="discount"
              size="sm"
              tone="secondary"
              label="Desconto"
              value={<Price cents={discountCents} sign="minus" tone="secondary" />}
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={rowTransition}
              className="overflow-hidden"
            />
          ) : null}
        </AnimatePresence>
        <KeyValueRow
          emphasis="total"
          size="base"
          label="Total"
          value={
            <Text as="span" tone="secondary">
              <motion.span className="tabular-nums" data-testid="bag-summary-total">
                {totalLabel}
              </motion.span>
            </Text>
          }
        />
      </DescriptionList>
      <Text size="xs" tone="subtle" className="mt-2">
        Taxa de entrega calculada no próximo passo
      </Text>
    </Card>
  )
}
